type RuntimeEnvironmentName = 'dev' | 'test' | 'staging' | 'prod';

interface BrowserRuntimeConfig {
  API_URL?: string;
  IMAGE_SERVER_URL?: string;
  APP_URL?: string;
  AUTH0_AUDIENCE?: string;
  AUTH0_DOMAIN?: string;
  AUTH0_CLIENT_ID?: string;
  ENV_NAME?: RuntimeEnvironmentName;
  MATOMO_CONTAINER_ID?: string;
  MATOMO_CUSTOM_DIMENSION_ACCOUNT_NUMBER_ID?: string;
  MATOMO_CUSTOM_DIMENSION_MODULE_ID?: string;
  MATOMO_CUSTOM_DIMENSION_PERMISSION_ID?: string;
  USETIFUL_TOKEN_KEY?: string;
  MAX_MONTH_DATE_RANGE?: string;
  SENTRY_DSN?: string;
  SENTRY_RELEASE?: string;
}

declare global {
  interface Window {
    __APP_CONFIG__?: BrowserRuntimeConfig;
  }
}

// eslint-disable-next-line no-underscore-dangle
const browserRuntimeConfig = typeof window === 'undefined' ? {} : (window.__APP_CONFIG__ ?? {});

function getRuntimeValue(value: string | undefined, fallback: string): string {
  return value && value.length > 0 ? value : fallback;
}

function getRuntimeNumberValue(value: string | undefined, fallback: string): number {
  const parsed = Number.parseInt(value ?? fallback, 10);
  return Number.isFinite(parsed) ? parsed : Number.parseInt(fallback, 10);
}

export const runtimeConfig = {
  API_URL: getRuntimeValue(browserRuntimeConfig.API_URL, import.meta.env.VITE_API_URL),
  IMAGE_SERVER_URL: getRuntimeValue(browserRuntimeConfig.IMAGE_SERVER_URL, import.meta.env.VITE_IMAGE_SERVER_URL),
  APP_URL: getRuntimeValue(browserRuntimeConfig.APP_URL, import.meta.env.VITE_APP_URL),
  AUTH0_AUDIENCE: getRuntimeValue(browserRuntimeConfig.AUTH0_AUDIENCE, import.meta.env.VITE_AUTH0_AUDIENCE),
  AUTH0_DOMAIN: getRuntimeValue(browserRuntimeConfig.AUTH0_DOMAIN, import.meta.env.VITE_AUTH0_DOMAIN),
  AUTH0_CLIENT_ID: getRuntimeValue(browserRuntimeConfig.AUTH0_CLIENT_ID, import.meta.env.VITE_AUTH0_CLIENT_ID),
  ENV_NAME: getRuntimeValue(browserRuntimeConfig.ENV_NAME, import.meta.env.VITE_ENV_NAME) as RuntimeEnvironmentName,
  MATOMO_CONTAINER_ID: getRuntimeValue(
    browserRuntimeConfig.MATOMO_CONTAINER_ID,
    import.meta.env.VITE_MATOMO_CONTAINER_ID
  ),
  MATOMO_CUSTOM_DIMENSION_ACCOUNT_NUMBER_ID: getRuntimeValue(
    browserRuntimeConfig.MATOMO_CUSTOM_DIMENSION_ACCOUNT_NUMBER_ID,
    import.meta.env.VITE_MATOMO_CUSTOM_DIMENSION_ACCOUNT_NUMBER_ID ?? ''
  ),
  MATOMO_CUSTOM_DIMENSION_MODULE_ID: getRuntimeValue(
    browserRuntimeConfig.MATOMO_CUSTOM_DIMENSION_MODULE_ID,
    import.meta.env.VITE_MATOMO_CUSTOM_DIMENSION_MODULE_ID ?? ''
  ),
  MATOMO_CUSTOM_DIMENSION_PERMISSION_ID: getRuntimeValue(
    browserRuntimeConfig.MATOMO_CUSTOM_DIMENSION_PERMISSION_ID,
    import.meta.env.VITE_MATOMO_CUSTOM_DIMENSION_PERMISSION_ID ?? ''
  ),
  USETIFUL_TOKEN_KEY: getRuntimeValue(browserRuntimeConfig.USETIFUL_TOKEN_KEY, import.meta.env.VITE_USETIFUL_TOKEN_KEY),
  MAX_MONTH_DATE_RANGE: getRuntimeNumberValue(
    browserRuntimeConfig.MAX_MONTH_DATE_RANGE,
    import.meta.env.VITE_MAX_MONTH_DATE_RANGE
  ),
  SENTRY_DSN: getRuntimeValue(browserRuntimeConfig.SENTRY_DSN, (import.meta.env.VITE_SENTRY_DSN as string) || ''),
  SENTRY_RELEASE: getRuntimeValue(browserRuntimeConfig.SENTRY_RELEASE, import.meta.env.VITE_SENTRY_RELEASE || ''),
} as const;
